import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { plans, PricingPlan } from "@/lib/constants";
import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Check, FileText, ShieldCheck } from "lucide-react";

export function Pricing() {
  return (
    <section className="py-20 relative" id="pricing">
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(124,58,237,0.08),transparent_70%)]"></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="mb-16"
        >
          <div className="inline-flex items-center px-3 py-1 mb-4 text-sm font-medium text-violet-800 bg-violet-100 rounded-full">
            Pricing
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Pay only for what you need, with no long-term commitments
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mx-auto"
        >
          <PricingCards />
        </motion.div>
      </div>
    </section>
  );
}

// Define the plan data

function PricingCards() {
  return (
    <div className="mx-auto mt-20 grid grid-cols-1 items-start gap-4 md:grid-cols-2 xl:grid-cols-4">
      {plans.map((plan) => (
        <PricingCard plan={plan} />
      ))}
    </div>
  );
}

function PricingCard({ plan }: { plan: PricingPlan }) {
  return (
    <Card className="bg-accent p-4 rounded-2xl text-left h-full" key={plan.id}>
      <div className="flex h-full flex-col justify-start gap-4">
        {/* Top Card Section (Price, Name, Button) */}
        <CardHeader className="bg-white p-4 shadow-input dark:bg-neutral-800 dark:shadow-[0px_-1px_0px_0px_var(--neutral-700)] rounded-2xl">
          {/* <div className="w-full rounded-2xl bg-white p-4 shadow-input dark:bg-neutral-800 dark:shadow-[0px_-1px_0px_0px_var(--neutral-700)] text-left"> */}
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-2">
              <p className="text-lg font-medium text-black dark:text-white">
                {plan.name}
              </p>
              <span className="inline-block rounded-md text-sm tracking-tight text-neutral-600 dark:text-neutral-200">
                {plan.subtitle}
              </span>
            </div>
            {/* Featured Tag - Conditional */}
            {/* {plan.isFeatured && (
          <div className="relative rounded-full bg-neutral-900 px-3 py-1 text-xs font-medium text-white dark:bg-white dark:text-black">
            <div className="absolute inset-x-0 bottom-0 mx-auto h-px w-3/4 bg-gradient-to-r from-transparent via-indigo-500 to-transparent" />
            Featured
          </div>
        )} */}
          </div>
          <div className="mt-8">
            <div className="flex items-end">
              <span className="text-lg font-bold text-neutral-500 dark:text-neutral-200">
                $
              </span>
              <div className="flex  gap-2 items-baseline justify-center">
                <span className="text-4xl font-bold text-neutral-800 dark:text-neutral-50 md:text-5xl">
                  {plan.price}
                </span>
                <span className="text-xl opacity-80 font-normal item-end">
                  per evaluation
                </span>
                {/* <div className="flex flex-col">
          <span className="mt-2 text-base text-gray-500 line-through dark:text-neutral-200">
            ${plan.originalPrice}
          </span>
          <span className="mt-1 inline-block rounded-md bg-white px-2 py-1 text-xs text-neutral-600 shadow-input dark:bg-neutral-900 dark:text-neutral-200">
            {plan.discount}% OFF
          </span>
        </div> */}
              </div>
            </div>
          </div>

          <Button
            className="w-full  mt-10 bg-gradient-to-b from-violet-500 to-violet-600"
            asChild
          >
            <Link to="/get-started" search={{ type: plan.id }}>
              {plan.buttonText}
            </Link>
          </Button>
          {/* </div> */}
        </CardHeader>

        {/* Features List Section */}
        <CardContent className="mt-1 px-4 group">
          {plan.features.map((feature, index) => (
            <div
              key={index}
              className="my-4 flex items-start justify-start gap-2 "
            >
              <div className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-neutral-700 ">
                <Check className="text-white " size={10} />
              </div>
              <div className="text-sm font-medium text-black dark:text-white text-left text-balance">
                {feature}
              </div>
            </div>
          ))}
        </CardContent>
        {/* "Included Features" Section - Conditional */}
        {/* {plan.baseFeaturesIncluded &&
          plan.baseFeaturesIncluded.length > 0 && (
            <>
              <div className="relative">
                <div className="h-px w-full bg-white dark:bg-neutral-950" />
                <div className="h-px w-full bg-neutral-200 dark:bg-neutral-800" />
                <div className="absolute inset-0 m-auto flex h-5 w-5 items-center justify-center rounded-xl bg-white shadow-[0px_-1px_0px_0px_var(--neutral-200)] dark:bg-neutral-800 dark:shadow-[0px_-1px_0px_0px_var(--neutral-700)]">
                  <PlusIcon />
                </div>
              </div>
              <div className="p-4">
                {plan.baseFeaturesIncluded.map((basePlanName, index) => (
                  <div
                    key={index}
                    className="my-4 flex items-start justify-start gap-2"
                  >
                    <div className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-indigo-600">
                      <CheckIcon />
                    </div>
                    <div className="text-sm font-medium text-black dark:text-white">
                      Everything in {basePlanName} Plan
                    </div>
                  </div>
                ))}
              </div>
            </>
          )} */}

        {/* Empty div for spacing - Rendered only if there's no "Included Features" section to maintain layout consistency */}
        {/* {(!plan.baseFeaturesIncluded ||
          plan.baseFeaturesIncluded.length === 0) && (
          <div className="p-4" />
        )} */}
        {/* {(plan.id === "image" || plan.id === "copy") && <div className="p-4" />} */}
      </div>
      {/* <CardFooter>
        <Button variant="link" asChild>
          <a href="mailto:creative@consumr.ai">Questions? Chat with us.</a>
        </Button>
      </CardFooter>{" "} */}
    </Card>
  );
}

export function PricingCardConfirmation({ plan }: { plan: PricingPlan }) {
  // Define features based on the example, map from plan.features later if needed
  const exampleFeatures = [
    "Visual appeal and attention-grabbing analysis",
    "Brand alignment and message clarity evaluation",
    "Color psychology and emotional response feedback",
    "Target audience resonance assessment",
    "Comparative benchmarking against industry standards",
    "Actionable optimization recommendations",
    "Detailed report delivered within 24 hours",
  ];

  return (
    <Card className="mb-6 shadow-sm w-full" key={plan.id}>
      <CardHeader className="pb-2">
        <CardTitle className="text-2xl">{plan.name}</CardTitle>
        <p className="text-gray-500 text-sm">{plan.subtitle}</p>
      </CardHeader>

      <CardContent>
        <div className="flex items-center mb-5 bg-gray-50 rounded-lg p-4 border border-gray-100">
          {/* <div className="w-24 h-24 bg-gray-200 rounded-md mr-4 overflow-hidden flex-shrink-0">
            <img
              src="/api/placeholder/100/100"
              alt="Your submitted creative"
              className="w-full h-full object-cover"
            />
          </div> */}
          <div className="flex-1">
            <Badge className="bg-purple-100 text-purple-600 hover:bg-purple-100 mb-2 capitalize">
              {plan.creativeType}
            </Badge>
            <h3 className="text-base font-semibold text-gray-800 mb-1">
              Your {plan.creativeType} will be assessed
            </h3>
            <p className="text-sm text-gray-600">
              Our system will analyze this {plan.creativeType} for
              effectiveness, audience appeal, and conversion potential.
            </p>
            {plan?.link && (
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={plan.link}
                className="inline-flex items-center text-purple-600 text-sm font-medium mt-2 hover:underline"
              >
                <FileText size={16} className="mr-1" /> View sample report
              </a>
            )}
          </div>
        </div>

        <div className="flex items-baseline mb-5 bg-gray-50 p-4 rounded-lg">
          <span className="text-xl text-gray-500 mr-1">$</span>
          <span className="text-5xl font-bold text-gray-800">{plan.price}</span>
          <span className="text-gray-500 text-base ml-2">
            one-time assessment fee
          </span>
        </div>

        {plan.lineItems.map((item) => (
          <div className="my-5 space-y-2.5">
            <div className="flex items-start">
              <item.icon
                size={16}
                className="text-purple-600 mr-2.5 mt-1 flex-shrink-0"
              />
              <div className="text-sm font-medium text-gray-800">
                {item.description}
              </div>
            </div>
          </div>
        ))}

        <div className="my-5">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">
            Your assessment report will include:
          </h3>
          <div className="space-y-3">
            {exampleFeatures.map((feature, index) => (
              <div key={index} className="flex items-start">
                <div className="w-5 h-5 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center mr-2.5 flex-shrink-0">
                  <Check size={12} />
                </div>
                <div className="text-sm text-gray-800 leading-snug">
                  {feature}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>

      <CardFooter className="text-center text-sm text-gray-500 justify-center">
        {/* Guarantee Section */}
        <div className="flex items-center text-sm text-gray-500 mt-3 pt-3 border-t border-gray-100">
          <ShieldCheck size={16} className="text-teal-600 mr-2 flex-shrink-0" />
          <div>
            100% satisfaction guarantee - If you&apos;re not satisfied,
            we&apos;ll refund your purchase
          </div>
        </div>
        {/* Questions?{" "}
        <a
          href="#"
          className="text-purple-600 font-medium hover:underline ml-1"
        >
          Chat with us
        </a> */}
      </CardFooter>
    </Card>
  );
}
